import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { repTargetsAPI, adminAPI } from '../api/client';
import { CURRENCY } from '../config/constants';
import { 
  TrendingUp, Target, Award, 
  Plus, X, Edit2, Calendar,
  Search, AlertOctagon, Users,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGlobalSearch } from '../context/SearchContext';
import { matchesGlobalSearch } from '../utils/globalSearch';
import { istYearMonth, formatIstMonthYear, formatVolumeM3, normalizeYearMonth } from '../utils/formatIst';

const normalizeTargetMonth = normalizeYearMonth;

const parseAchievedM3 = (progressRow) => {
  if (!progressRow || typeof progressRow !== 'object') return 0;
  const raw =
    progressRow.achieved_m3 ??
    progressRow.sales_volume_m3 ??
    progressRow.sales_achieved ??
    progressRow.total_sales_volume ??
    0;
  return parseFloat(raw) || 0;
};

const TargetManagement = () => {
  const [targets, setTargets] = useState([]);
  const [reps, setReps] = useState([]);
  const [teamSummary, setTeamSummary] = useState([]);
  const [progressByRepId, setProgressByRepId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [confirmOverride, setConfirmOverride] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(istYearMonth());
  const [searchTerm, setSearchTerm] = useState('');
  const [achievementFilter, setAchievementFilter] = useState('all');
  const { query: globalSearch } = useGlobalSearch();

  const filteredTeamSummary = useMemo(() => {
    return teamSummary.filter((row) => {
      const pct = row.achievement_percent;
      const hasTarget = row.has_target;

      if (achievementFilter === 'met' && (!hasTarget || pct == null || pct < 100)) return false;
      if (achievementFilter === 'below' && (!hasTarget || pct == null || pct >= 100)) return false;
      if (achievementFilter === 'no_target' && hasTarget) return false;
      if (achievementFilter === 'has_target' && !hasTarget) return false;

      const statusLabel = !hasTarget
        ? 'No target'
        : pct >= 100
          ? 'Met'
          : 'Below target';
      const searchFields = [
        row.rep_name,
        row.target_m3,
        row.achieved_m3,
        pct,
        statusLabel,
        hasTarget ? 'target set' : 'no target',
      ];
      if (!matchesGlobalSearch(globalSearch, searchFields)) return false;
      if (searchTerm && !matchesGlobalSearch(searchTerm, searchFields)) return false;
      return true;
    });
  }, [teamSummary, achievementFilter, searchTerm, globalSearch]);

  const hasActiveFilters = achievementFilter !== 'all' || searchTerm.trim() !== '';
  const clearFilters = () => {
    setAchievementFilter('all');
    setSearchTerm('');
  };

  const teamStats = useMemo(() => {
    const withTarget = filteredTeamSummary.filter((r) => r.has_target);
    const totalTarget = filteredTeamSummary.reduce((s, r) => s + (parseFloat(r.target_m3) || 0), 0);
    const totalAchieved = filteredTeamSummary.reduce((s, r) => s + (parseFloat(r.achieved_m3) || 0), 0);
    const avgPct = withTarget.length
      ? Math.round(
          withTarget.reduce((s, r) => s + (r.achievement_percent ?? 0), 0) / withTarget.length
        )
      : 0;
    return {
      repCount: filteredTeamSummary.length,
      withTargetCount: withTarget.length,
      totalTarget,
      totalAchieved,
      avgPct,
    };
  }, [filteredTeamSummary]);

  const filteredTargets = useMemo(() => {
    const monthKey = normalizeTargetMonth(selectedMonth);
    return targets
      .filter((target) => normalizeTargetMonth(target.target_month) === monthKey)
      .filter((target) =>
        matchesGlobalSearch(globalSearch, [
          target.rep_name,
          target.target_month,
          target.monthly_sales_target_m3,
          target.incentive_rate_per_m3,
          target.penalty_rate_per_m3,
          parseAchievedM3(progressByRepId[target.rep_id]),
        ])
      );
  }, [targets, selectedMonth, globalSearch, progressByRepId]);

  const [formData, setFormData] = useState({
    rep_id: '',
    target_month: istYearMonth(),
    monthly_sales_target_m3: '300',
    incentive_rate_per_m3: '5',
    incentive_rate_max_per_m3: '9',
    penalty_rate_per_m3: '50',
    status: 'active',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const monthKey = normalizeTargetMonth(selectedMonth);
      const [targetsRes, repsRes, summaryRes] = await Promise.all([
        repTargetsAPI.getAll(),
        adminAPI.getAllReps(),
        repTargetsAPI.getMonthSummary(monthKey),
      ]);
      const targetsData = targetsRes.data.data || [];
      const repsData = repsRes.data.data || [];
      const summaryData = summaryRes.data?.data || [];

      setTargets(targetsData);
      setReps(repsData);

      const progressMap = Object.fromEntries(
        summaryData.map((row) => [
          row.rep_id,
          {
            achieved_m3: row.achieved_m3,
            sales_volume_m3: row.achieved_m3,
            sales_achieved: row.achieved_m3,
            target_m3: row.target_m3,
            bonus_earned: row.bonus_earned,
            penalty_amount: row.penalty_amount,
            net_compensation: row.net_compensation,
          },
        ])
      );
      setProgressByRepId(progressMap);
      setTeamSummary(summaryData);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingId && formData.rep_id && formData.target_month) {
      const existing = targets.find(
        t => t.rep_id === parseInt(formData.rep_id) && t.target_month === formData.target_month
      );
      if (existing && !confirmOverride) {
        setDuplicateWarning(existing);
        setConfirmOverride(false);
        return;
      }
    }
    
    setConfirmOverride(false);
    setDuplicateWarning(null);
    
    try {
      if (editingId) {
        const response = await repTargetsAPI.update(editingId, { ...formData, updated_by: 1 });
        if (response.data.success) { resetForm(); await fetchData(); }
      } else {
        const response = await repTargetsAPI.create({ ...formData, updated_by: 1, force_override: confirmOverride });
        if (response.data.success) { resetForm(); await fetchData(); }
      }
    } catch (err) {
      setError(err.message || 'Failed to save targets');
    }
  };

  const handleEdit = (target) => {
    setEditingId(target.id);
    setFormData({
      rep_id: target.rep_id,
      target_month: target.target_month || istYearMonth(),
      monthly_sales_target_m3: target.monthly_sales_target_m3,
      incentive_rate_per_m3: target.incentive_rate_per_m3,
      incentive_rate_max_per_m3: target.incentive_rate_max_per_m3,
      penalty_rate_per_m3: target.penalty_rate_per_m3,
      status: target.status === 'inactive' ? 'inactive' : 'active',
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      rep_id: '',
      target_month: istYearMonth(),
      monthly_sales_target_m3: '300',
      incentive_rate_per_m3: '5',
      incentive_rate_max_per_m3: '9',
      penalty_rate_per_m3: '50',
      status: 'active',
    });
    setDuplicateWarning(null);
    setConfirmOverride(false);
    setShowForm(false);
  };

  if (loading && targets.length === 0) return (
    <div className="flex flex-col justify-center items-center h-64 gap-3">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-bold animate-pulse text-sm">Syncing Targets...</p>
    </div>
  );

  return (
    <div className="admin-page space-y-8 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Target Management</h1>
          <p className="text-slate-500 font-medium">Configure and track sales objectives for your team.</p>
        </div>
        <div className="flex items-center gap-3">
            <div className="relative group">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                />
            </div>
            <button
                onClick={() => setShowForm(!showForm)}
                className={`btn-premium ${showForm ? 'bg-slate-100 text-slate-700' : 'bg-blue-600 text-white shadow-lg shadow-blue-500/20 hover:bg-blue-700'}`}
            >
                {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {showForm ? 'Cancel' : 'New Target'}
            </button>
        </div>
      </div>

      {/* Team summary — all reps for selected month */}
      {teamSummary.length > 0 && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            {[
              { title: 'Reps', value: teamStats.repCount, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
              { title: 'With target set', value: teamStats.withTargetCount, icon: Target, color: 'text-indigo-600', bg: 'bg-indigo-50' },
              { title: 'Avg achievement', value: teamStats.withTargetCount ? `${teamStats.avgPct}%` : '—', icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { title: 'Team achieved / target', value: `${formatVolumeM3(teamStats.totalAchieved)} / ${formatVolumeM3(teamStats.totalTarget)} m³`, icon: Award, color: 'text-amber-600', bg: 'bg-amber-50' },
            ].map((stat, i) => (
              <div key={i} className="glass-card p-5 flex flex-col gap-3">
                <div className={`p-2.5 rounded-xl ${stat.bg} ${stat.color} w-fit`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500">{stat.title}</p>
                  <h3 className="text-xl font-extrabold text-slate-900 tracking-tight">{stat.value}</h3>
                </div>
              </div>
            ))}
          </motion.div>

          <div className="glass-card p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-slate-800">
                Team target vs achieved — {formatIstMonthYear(selectedMonth)}
              </h2>
            </div>

            <div className="admin-toolbar">
              <div className="admin-toolbar-field">
                <label className="block text-xs font-medium text-slate-600 mb-1">Search</label>
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rep name, target, achieved, status…"
                    className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="admin-toolbar-field">
                <label className="block text-xs font-medium text-slate-600 mb-1">Achievement</label>
                <select
                  value={achievementFilter}
                  onChange={(e) => setAchievementFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="all">All reps</option>
                  <option value="has_target">Has target</option>
                  <option value="met">Target met (100%+)</option>
                  <option value="below">Below target</option>
                  <option value="no_target">No target set</option>
                </select>
              </div>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 h-[42px]"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500">
              Showing {filteredTeamSummary.length} of {teamSummary.length} rep{teamSummary.length === 1 ? '' : 's'}
              {' · '}Achieved volume is from order line items (pending, confirmed, delivered)
            </p>

            {filteredTeamSummary.length === 0 ? (
              <p className="text-center text-slate-500 py-8 font-medium">No reps match your filters.</p>
            ) : (
              <>
                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                  {filteredTeamSummary.map((row) => {
                    const targetM3 = parseFloat(row.target_m3) || 0;
                    const achieved = parseFloat(row.achieved_m3) || 0;
                    const pct = row.achievement_percent;
                    const barPct = targetM3 > 0 && pct != null ? Math.min(pct, 100) : 0;
                    return (
                      <div key={row.rep_id} className="space-y-1.5">
                        <div className="flex items-center justify-between gap-3 text-sm flex-wrap">
                          <span className="font-bold text-slate-800">{row.rep_name}</span>
                          <span className="font-semibold text-slate-600">
                            {formatVolumeM3(achieved)} / {row.has_target ? `${formatVolumeM3(targetM3)} m³` : '—'}
                            {pct != null && (
                              <span className={`ml-2 ${pct >= 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                ({pct}%)
                              </span>
                            )}
                            {!row.has_target && (
                              <span className="ml-2 text-slate-400 text-xs">No target</span>
                            )}
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              !row.has_target
                                ? 'bg-slate-300'
                                : pct >= 100
                                  ? 'bg-emerald-500'
                                  : 'bg-blue-500'
                            }`}
                            style={{ width: `${barPct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="admin-table-scroll border-t border-slate-100 pt-4">
                  <table className="admin-data-table text-sm w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold text-slate-600">Representative</th>
                        <th className="px-4 py-2 text-right font-semibold text-slate-600">Target (m³)</th>
                        <th className="px-4 py-2 text-right font-semibold text-slate-600">Achieved (m³)</th>
                        <th className="px-4 py-2 text-right font-semibold text-slate-600">Progress</th>
                        <th className="px-4 py-2 text-left font-semibold text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredTeamSummary.map((row) => {
                        const targetM3 = parseFloat(row.target_m3) || 0;
                        const achieved = parseFloat(row.achieved_m3) || 0;
                        const pct = row.achievement_percent;
                        return (
                          <tr key={row.rep_id} className="hover:bg-blue-50/30">
                            <td className="px-4 py-3 font-bold text-slate-800">{row.rep_name}</td>
                            <td className="px-4 py-3 text-right text-slate-700">
                              {row.has_target ? formatVolumeM3(targetM3) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-emerald-700">
                              {formatVolumeM3(achieved)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {pct != null ? (
                                <span className={pct >= 100 ? 'text-emerald-600 font-bold' : 'text-amber-600 font-bold'}>
                                  {pct}%
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {!row.has_target ? (
                                <span className="text-xs font-bold uppercase tracking-wide text-slate-500 bg-slate-100 px-2 py-1 rounded">
                                  No target
                                </span>
                              ) : pct >= 100 ? (
                                <span className="text-xs font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-2 py-1 rounded">
                                  Met
                                </span>
                              ) : (
                                <span className="text-xs font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-1 rounded">
                                  Below target
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 min-w-0">
        {/* Form and List Container */}
        <div className={`lg:col-span-${showForm ? '7' : '12'} space-y-8 transition-all duration-500 min-w-0`}>
            {/* Targets Table */}
            <div className="glass-card admin-table-shell">
                <div className="p-6 border-b border-slate-100">
                    <h2 className="text-lg font-bold text-slate-800">
                      Target configuration — {formatIstMonthYear(selectedMonth)}
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">Edit incentive rates and targets per rep (configured rows only).</p>
                </div>
                <div className="admin-table-scroll">
                    <table className="admin-data-table-wide">
                        <thead className="bg-slate-50/50">
                            <tr>
                                <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest">Representative</th>
                                <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest">Target</th>
                                <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest">Achieved</th>
                                <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest">Progress</th>
                                <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest text-center">Incentive</th>
                                <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest">Status</th>
                                <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-widest">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredTargets.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-slate-400 font-medium">
                                      {targets.length === 0
                                        ? 'No targets configured for any month.'
                                        : targets.some((t) => normalizeTargetMonth(t.target_month) === normalizeTargetMonth(selectedMonth))
                                          ? 'No targets match your search.'
                                          : `No targets configured for ${formatIstMonthYear(selectedMonth)}.`}
                                    </td>
                                </tr>
                            ) : (
                                filteredTargets.map((target) => {
                                    const progress = progressByRepId[target.rep_id] || {};
                                    const achieved = parseAchievedM3(progress);
                                    const targetM3 = parseFloat(progress.target_m3 ?? target.monthly_sales_target_m3) || 0;
                                    const progressPct = targetM3 > 0
                                      ? Math.round((achieved / targetM3) * 100)
                                      : 0;
                                    return (
                                    <tr key={target.id} className="hover:bg-blue-50/30 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs">
                                                    {target.rep_name?.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-800">{target.rep_name}</p>
                                                    <p className="text-[10px] font-bold text-slate-400">{target.target_month}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-sm font-bold text-slate-700">{formatVolumeM3(targetM3)} m³</p>
                                            <p className="text-[10px] font-bold text-slate-400">Target</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-sm font-bold text-emerald-700">{formatVolumeM3(achieved)} m³</p>
                                            <p className="text-[10px] font-bold text-slate-400">Recorded sales</p>
                                        </td>
                                        <td className="px-6 py-4 min-w-[140px]">
                                            <div className="flex items-center gap-2">
                                              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                  className={`h-full rounded-full ${progressPct >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                                                  style={{ width: `${Math.min(progressPct, 100)}%` }}
                                                />
                                              </div>
                                              <span className="text-xs font-bold text-slate-600 w-10 text-right">{progressPct}%</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="text-sm font-bold text-blue-600">{CURRENCY.SYMBOL}{target.incentive_rate_per_m3}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${target.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {target.status === 'inactive' ? 'inactive' : 'active'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button 
                                                onClick={() => handleEdit(target)}
                                                className="p-2 hover:bg-blue-100 rounded-lg text-blue-600 transition-colors opacity-0 group-hover:opacity-100"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        {/* Form Panel */}
        <AnimatePresence>
            {showForm && (
                <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="lg:col-span-5"
                >
                    <div className="glass-card p-8 sticky top-8">
                        <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                            {editingId ? <Edit2 className="w-5 h-5 text-blue-600" /> : <Plus className="w-5 h-5 text-blue-600" />}
                            {editingId ? 'Edit Sales Target' : 'Create New Target'}
                        </h3>

                        <form onSubmit={handleSubmit} className="admin-page space-y-6">
                            <div className="space-y-2">
                                <label className="text-sm font-bold text-slate-700">Select Representative</label>
                                <select
                                    value={formData.rep_id}
                                    onChange={(e) => setFormData({ ...formData, rep_id: e.target.value })}
                                    disabled={editingId !== null}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-medium text-slate-800 disabled:opacity-50"
                                    required
                                >
                                    <option value="">Choose a Rep</option>
                                    {reps.map(rep => (
                                        <option key={rep.id} value={rep.id}>{rep.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-slate-700">Target Month</label>
                                    <input
                                        type="month"
                                        value={formData.target_month}
                                        onChange={(e) => setFormData({ ...formData, target_month: e.target.value })}
                                        disabled={editingId !== null}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none disabled:opacity-50"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-slate-700">Sales Target (m³)</label>
                                    <input
                                        type="number"
                                        value={formData.monthly_sales_target_m3}
                                        onChange={(e) => setFormData({ ...formData, monthly_sales_target_m3: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-medium outline-none"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2 pt-4 border-t border-slate-100">
                                <label className="text-sm font-bold text-slate-700">Target Mode</label>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, status: 'active' })}
                                        className={`flex-1 px-4 py-3 rounded-xl font-bold text-sm transition-colors ${
                                          formData.status === 'active'
                                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                                            : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                                        }`}
                                    >
                                        Active
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, status: 'inactive' })}
                                        className={`flex-1 px-4 py-3 rounded-xl font-bold text-sm transition-colors ${
                                          formData.status === 'inactive'
                                            ? 'bg-slate-600 text-white shadow-lg shadow-slate-500/20'
                                            : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                                        }`}
                                    >
                                        Inactive
                                    </button>
                                </div>
                                <p className="text-xs text-slate-500">
                                  {formData.status === 'inactive'
                                    ? 'Inactive: no target bonus or penalty. Product incentives still apply.'
                                    : 'Active: target bonus and penalty apply on top of product incentives.'}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-slate-700 text-blue-600">Base Rate ({CURRENCY.SYMBOL})</label>
                                    <input
                                        type="number"
                                        value={formData.incentive_rate_per_m3}
                                        onChange={(e) => setFormData({ ...formData, incentive_rate_per_m3: e.target.value })}
                                        className="w-full px-4 py-3 bg-blue-50/30 border border-blue-100 rounded-xl font-bold text-blue-700 outline-none"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-slate-700 text-rose-600">Penalty Rate ({CURRENCY.SYMBOL})</label>
                                    <input
                                        type="number"
                                        value={formData.penalty_rate_per_m3}
                                        onChange={(e) => setFormData({ ...formData, penalty_rate_per_m3: e.target.value })}
                                        className="w-full px-4 py-3 bg-rose-50/30 border border-rose-100 rounded-xl font-bold text-rose-700 outline-none"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="flex-1 px-6 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                                >
                                    Discard
                                </button>
                                <button
                                    type="submit"
                                    className="flex-3 bg-blue-600 text-white font-bold px-8 py-3 rounded-xl shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-colors"
                                >
                                    {editingId ? 'Save Changes' : 'Create Target'}
                                </button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
      </div>

      {error && (
        <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-8 right-8 bg-rose-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 z-50"
        >
            <div className="bg-white/20 p-2 rounded-lg"><AlertOctagon className="w-5 h-5" /></div>
            <p className="font-bold">{error}</p>
            <button onClick={() => setError(null)} className="ml-4 hover:scale-110 transition-transform"><X className="w-4 h-4" /></button>
        </motion.div>
      )}
    </div>
  );
};

export default TargetManagement;

